import type { Properties } from 'posthog-js';
import posthog from '@/composables/usePostHog.js';
import logger from '@/util/logging/logger.js';

type ErrorSeverity = 'debug' | 'info' | 'warning' | 'error' | 'fatal';
type ErrorDetailValue = string | number | boolean | null | ErrorDetailValue[] | { [key: string]: ErrorDetailValue };

export interface HttpErrorContext {
	method?: string | null | undefined;
	url?: string | null | undefined;
	status?: number | null | undefined;
	statusText?: string | null | undefined;
	requestId?: string | null | undefined;
}

export interface ErrorReportContext {
	source: string;
	operation: string;
	domain?: string;
	handled?: boolean;
	severity?: ErrorSeverity;
	component?: string | null;
	routeName?: string | null;
	routePattern?: string | null;
	http?: HttpErrorContext;
	details?: Record<string, unknown>;
	fingerprint?: string;
}

export interface BuiltErrorReport {
	exception: Error;
	fingerprint: string;
	properties: Properties;
}

interface ExtractedError {
	code: string | null;
	message: string;
	name: string;
	stack: string | null;
	http: Required<HttpErrorContext>;
	validationFields: string[];
}

const REDACTED = '[REDACTED]';
const MAX_STRING_LENGTH = 500;
const MAX_DETAIL_DEPTH = 4;
const MAX_ARRAY_LENGTH = 20;
const PRIMITIVE_DEDUPE_WINDOW_MS = 1_000;
const SENSITIVE_KEY_PATTERN = /authorization|cookie|password|passwd|secret|token|api[-_]?key|session|credit|card|cvv|cvc/i;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_IDENTIFIER_PATTERN = /^(?:[0-9a-f]{12,}|[a-z0-9_-]{24,})$/i;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const capturedObjects = new WeakSet();
const recentPrimitiveFingerprints = new Map<string, number>();

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

function readString(value: unknown): string | null {
	if (typeof value !== 'string') return null;
	const normalized = value.trim();
	return normalized ? normalized : null;
}

function readNumber(value: unknown): number | null {
	return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function truncate(value: string, maximumLength = MAX_STRING_LENGTH): string {
	return value.length <= maximumLength ? value : `${value.slice(0, maximumLength - 1)}…`;
}

function firstString(...values: unknown[]): string | null {
	for (const value of values) {
		const candidate = readString(value);
		if (candidate) return candidate;
	}
	return null;
}

function sanitizeDetail(value: unknown, key = '', depth = 0, seen = new WeakSet()): ErrorDetailValue {
	if (SENSITIVE_KEY_PATTERN.test(key)) return REDACTED;
	if (value === null || value === undefined) return null;
	if (typeof value === 'string') return truncate(value);
	if (typeof value === 'number') return Number.isFinite(value) ? value : String(value);
	if (typeof value === 'boolean') return value;
	if (typeof value === 'bigint' || typeof value === 'symbol' || typeof value === 'function') return truncate(String(value));
	if (!isRecord(value)) return '[Unsupported value]';
	if (depth >= MAX_DETAIL_DEPTH) return '[Truncated]';
	if (seen.has(value)) return '[Circular]';

	seen.add(value);
	if (Array.isArray(value)) {
		return value.slice(0, MAX_ARRAY_LENGTH).map((item) => sanitizeDetail(item, key, depth + 1, seen));
	}

	return Object.fromEntries(Object.entries(value).map(([entryKey, entryValue]) => [entryKey, sanitizeDetail(entryValue, entryKey, depth + 1, seen)]));
}

function getCurrentRoutePattern(): string | null {
	if (typeof window === 'undefined') return null;
	return normalizePath(window.location.pathname);
}

export function normalizePath(url: string | null | undefined): string | null {
	const candidate = readString(url);
	if (!candidate) return null;

	let pathname: string;
	try {
		pathname = new URL(candidate, 'https://error-reporting.invalid').pathname;
	} catch {
		pathname = candidate.split(/[?#]/u, 1)[0] ?? candidate;
	}

	const withoutApiPrefix = pathname.replace(/^\/api\/v\d+(?=\/|$)/iu, '');
	const normalizedSegments = withoutApiPrefix
		.split('/')
		.filter(Boolean)
		.map((segment) => {
			if (/^\d+$/u.test(segment)) return ':id';
			if (UUID_PATTERN.test(segment)) return ':uuid';
			if (ISO_DATE_PATTERN.test(segment)) return ':date';
			if (LONG_IDENTIFIER_PATTERN.test(segment)) return ':token';
			return segment;
		});

	return `/${normalizedSegments.join('/')}` || '/';
}

function normalizeMessageForFingerprint(message: string): string {
	return truncate(
		message
			.toLowerCase()
			.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/giu, ':uuid')
			.replace(/\b\d{4}-\d{2}-\d{2}(?:t[^\s]+)?\b/giu, ':date')
			.replace(/\b\d+(?:\.\d+)?\b/gu, ':number')
			.replace(/\s+/gu, ' ')
			.trim(),
		160
	);
}

function fingerprintPart(value: unknown, fallback = 'none'): string {
	const candidate = readString(value);
	return truncate(candidate?.toLowerCase().replace(/\s+/gu, '_') ?? fallback, 160);
}

function extractError(error: unknown, context: ErrorReportContext): ExtractedError {
	const root = isRecord(error) ? error : {};
	const response = isRecord(root['response']) ? root['response'] : {};
	const responseData = isRecord(response['data']) ? response['data'] : {};
	const config = isRecord(root['config']) ? root['config'] : isRecord(response['config']) ? response['config'] : {};
	const responseDataText = typeof response['data'] === 'string' ? response['data'] : null;
	const validationFields = isRecord(responseData['fieldErrors']) ? Object.keys(responseData['fieldErrors']).sort() : [];

	const message = firstString(responseData['message'], responseData['error'], responseData['detail'], responseData['title'], responseDataText, root['message'], error) ?? `Unknown error during ${context.operation}`;
	const name = firstString(root['name']) ?? (response['status'] ? 'HttpError' : 'Error');
	const code = firstString(responseData['code'], responseData['errorCode'], root['code']);
	const status = context.http?.status ?? readNumber(response['status']);
	const method = context.http?.method ?? firstString(config['method']);
	const url = context.http?.url ?? firstString(config['url']);
	const headers = isRecord(response['headers']) ? response['headers'] : {};
	const requestId = context.http?.requestId ?? firstString(headers['x-request-id'], headers['x-correlation-id']);

	return {
		code,
		message: truncate(message),
		name: truncate(name, 100),
		stack: firstString(root['stack']),
		http: {
			method: method?.toUpperCase() ?? null,
			url,
			status,
			statusText: context.http?.statusText ?? firstString(response['statusText']),
			requestId
		},
		validationFields
	};
}

function buildFingerprint(extracted: ExtractedError, context: ErrorReportContext): string {
	if (readString(context.fingerprint)) return truncate(context.fingerprint as string, 500);

	const endpoint = normalizePath(extracted.http.url);
	return [
		'v2',
		`source:${fingerprintPart(context.source)}`,
		`domain:${fingerprintPart(context.domain)}`,
		`operation:${fingerprintPart(context.operation)}`,
		`type:${fingerprintPart(extracted.name)}`,
		`code:${fingerprintPart(extracted.code ?? extracted.http.status)}`,
		`request:${fingerprintPart(extracted.http.method)}_${fingerprintPart(endpoint)}`,
		`message:${fingerprintPart(normalizeMessageForFingerprint(extracted.message))}`
	].join('|');
}

function createSpecificException(extracted: ExtractedError, context: ErrorReportContext): Error {
	const scope = [context.source, context.domain, context.operation].filter(Boolean).join(':');
	const request = [extracted.http.method, normalizePath(extracted.http.url), extracted.http.status].filter((value) => value !== null && value !== undefined).join(' ');
	const prefix = [scope, request].filter(Boolean).join(' | ');
	const exception = new Error(`[${prefix}] ${extracted.message}`);
	exception.name = extracted.name;

	// Keep the original frames while making the PostHog issue title operation-specific.
	if (extracted.stack) {
		exception.stack = extracted.stack;
	}

	return exception;
}

export function buildErrorReport(error: unknown, context: ErrorReportContext): BuiltErrorReport {
	const extracted = extractError(error, context);
	const fingerprint = buildFingerprint(extracted, context);
	const routePattern = context.routePattern ?? getCurrentRoutePattern();
	const properties: Properties = {
		$exception_fingerprint: fingerprint,
		$exception_level: context.severity ?? 'error',
		error_issue_key: fingerprint,
		error_source: context.source,
		error_domain: context.domain ?? 'application',
		error_operation: context.operation,
		error_handled: context.handled ?? true,
		error_original_type: extracted.name,
		error_original_message: extracted.message,
		error_code: extracted.code,
		component: context.component ?? null,
		route_name: context.routeName ?? null,
		route_pattern: routePattern,
		http_method: extracted.http.method,
		http_route: normalizePath(extracted.http.url),
		http_status: extracted.http.status,
		http_status_text: extracted.http.statusText,
		request_id: extracted.http.requestId,
		validation_fields: extracted.validationFields,
		error_details: sanitizeDetail(context.details ?? {})
	};

	return {
		exception: createSpecificException(extracted, context),
		fingerprint,
		properties
	};
}

function shouldDeduplicate(error: unknown, fingerprint: string): boolean {
	if (isRecord(error) || typeof error === 'function') {
		if (capturedObjects.has(error)) return true;
		capturedObjects.add(error);
		return false;
	}

	const now = Date.now();
	const previousCapture = recentPrimitiveFingerprints.get(fingerprint);
	recentPrimitiveFingerprints.set(fingerprint, now);

	for (const [key, capturedAt] of recentPrimitiveFingerprints) {
		if (now - capturedAt > PRIMITIVE_DEDUPE_WINDOW_MS) recentPrimitiveFingerprints.delete(key);
	}

	return previousCapture !== undefined && now - previousCapture <= PRIMITIVE_DEDUPE_WINDOW_MS;
}

/**
 * Sends one privacy-conscious, operation-specific exception to PostHog.
 * The original object is deduplicated across API, Pinia, Vue, and window handlers.
 */
export function reportError(error: unknown, context: ErrorReportContext): boolean {
	try {
		const report = buildErrorReport(error, context);
		if (shouldDeduplicate(error, report.fingerprint)) return false;
		posthog.captureException(report.exception, report.properties);
		return true;
	} catch (reportingError) {
		logger.warn('[Error reporting] Failed to capture exception', {
			source: context.source,
			operation: context.operation,
			error: reportingError
		});
		return false;
	}
}
